import useDebounce from "@hooks/useDebounce";
import { useDocument } from "@hooks/useDocument";
import { useWindow } from "@hooks/useWindow";
import { useCallback, useEffect, useRef } from "react";
const LocalNavigationJS = () => {
    const myDocument = useDocument();
    const myWindow = useWindow();
    const lastSavedScrollValueRef = useRef(0);
    const { debounceFn } = useDebounce({ delay: 100 });
    const localNavFixedClassName = "local-navigation--fixed";
    const headerMenuClassName = "hidden";
    const headerMenuID = "#header";
    const scrollHandler = () => {
        const localNavEl = myDocument.getElementById("local-nav");
        const localNavList = Array.from(localNavEl.children).find((elem) => elem.nodeName === "UL");
        const currentScrollValue = myWindow.pageYOffset || myDocument.documentElement.scrollTop;
        const userScrolledUp = currentScrollValue < lastSavedScrollValueRef.current;
        const userScrolledDown = currentScrollValue > lastSavedScrollValueRef.current;
        const headerMenu = myDocument.querySelector(headerMenuID);
        // skip additional logic if:
        // - local nav is not in document
        // - local nav is in document but list is empty
        if (!headerMenu || !localNavEl || !localNavList || !(localNavList === null || localNavList === void 0 ? void 0 : localNavList.hasChildNodes())) {
            return;
        }
        const headerMenuHeight = headerMenu === null || headerMenu === void 0 ? void 0 : headerMenu.offsetHeight;
        const localNavInitialPosition = localNavEl.getBoundingClientRect().top;
        const slideUp = () => {
            headerMenu.classList.remove(headerMenuClassName);
            localNavEl.classList.remove(localNavFixedClassName);
        };
        const slideDown = () => {
            headerMenu.classList.add(headerMenuClassName);
            localNavEl.classList.add(localNavFixedClassName);
        };
        const userIsNotAtTheBeginningOfPage = myWindow.innerHeight + myWindow.scrollY + headerMenuHeight >= myDocument.body.offsetHeight;
        if (userScrolledDown && localNavInitialPosition <= headerMenuHeight) {
            slideDown();
        }
        else if (userScrolledUp && !userIsNotAtTheBeginningOfPage) {
            slideUp();
        }
        else if (userScrolledUp && userIsNotAtTheBeginningOfPage && headerMenu.style.display === "none") {
            slideUp();
        }
        lastSavedScrollValueRef.current = Math.max(currentScrollValue, 0);
    };
    const debouncedScrollHandler = debounceFn(scrollHandler);
    /**
     * Gets list of ids that are used as target
     * in the local navigation widget.
     * @returns
     */
    const getIdsInNavigation = useCallback(() => {
        if (!myDocument) {
            return [];
        }
        const localNavItems = Array.from(myDocument.getElementsByClassName("local-nav-item"));
        const idList = localNavItems.flatMap((item) => (item.children.length > 0 && !!item.children[0] ? item.children[0].getAttribute("href") : "")).filter(Boolean);
        if (idList.length === 0) {
            return [];
        }
        // remove hash (#) from id
        return idList.flatMap((hash) => hash.split("#").filter(Boolean));
    }, [myDocument]);
    useEffect(() => {
        const sub = myWindow && myWindow.addEventListener("scroll", debouncedScrollHandler);
        return () => sub;
    }, [myWindow, debouncedScrollHandler]);
    useEffect(() => {
        if (!!myDocument) {
            const idList = getIdsInNavigation();
            // list of elements that are targetted by the local navigation
            const sections = idList.map((id) => myDocument.getElementById(id)).filter(Boolean);
            if (sections.length > 0) {
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        const navItem = myDocument.querySelector(`a[href="#${entry.target.id}"]`);
                        if (entry.isIntersecting) {
                            navItem.classList.add("active");
                        }
                        else {
                            navItem.classList.remove("active");
                        }
                    });
                }, {
                    root: myDocument,
                    rootMargin: "-40% 0px -80% 0px"
                });
                sections.forEach((section) => observer.observe(section));
            }
        }
    }, [myDocument]);
    // In progress: Improvements for case when anchor was clicked and user try to click again (jumping issue)
    // Postpone for now.
    // const clickHandler = (event: MouseEvent) => {
    //   const eventTarget = event.currentTarget as HTMLAnchorElement;
    //   const href = eventTarget.getAttribute("href");
    //   if (myWindow.location.hash === href) {
    //     event.preventDefault();
    //   }
    // };
    // useEffect(() => {
    //   const sub =
    //     myDocument &&
    //     document.querySelectorAll("#local-nav .animated-link").forEach((el: HTMLAnchorElement) => {
    //       el.addEventListener("click", (ev) => clickHandler(ev));
    //     });
    //   return () => sub;
    // }, [myDocument]);
    return null;
};
export default LocalNavigationJS;
